import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, FileDown, Printer } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAVY = "#152C5B";
const rupee = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function numToWords(num) {
  const n = Math.round(Number(num || 0));
  if (n === 0) return "Zero Rupees Only";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => x < 20 ? a[x] : `${b[Math.floor(x / 10)]}${x % 10 ? " " + a[x % 10] : ""}`;
  const three = (x) => {
    const h = Math.floor(x / 100), r = x % 100;
    return `${h ? a[h] + " Hundred" + (r ? " " : "") : ""}${r ? two(r) : ""}`;
  };
  let words = "", rem = n;
  const crore = Math.floor(rem / 10000000); rem %= 10000000;
  const lakh = Math.floor(rem / 100000); rem %= 100000;
  const thousand = Math.floor(rem / 1000); rem %= 1000;
  const hundred = rem;
  if (crore) words += `${two(crore)} Crore `;
  if (lakh) words += `${two(lakh)} Lakh `;
  if (thousand) words += `${two(thousand)} Thousand `;
  if (hundred) words += three(hundred);
  return `${words.trim()} Rupees Only`;
}

export default function InvoiceReceipt() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [store, setStore] = useState({ name: "Digital Yantra", address: "", phone: "", email: "", gstin: "", state: "", state_code: "" });
  const [bank, setBank] = useState({ show: false });
  const [declaration, setDeclaration] = useState("");
  const [signature, setSignature] = useState("");
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageSize, setPageSize] = useState("A4");
  const printRef = useRef(null);

  const printNow = (size) => {
    setPageSize(size);
    setTimeout(() => { window.print(); setTimeout(() => setPageSize("A4"), 400); }, 120);
  };

  const autoPrinted = useRef(false);
  useEffect(() => {
    if (inv && !autoPrinted.current && searchParams.get("print") === "a5") {
      autoPrinted.current = true;
      printNow("A5");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv]);

  useEffect(() => {
    (async () => {
      try {
        const [i, st, bk, cfg] = await Promise.all([
          api.get(`/invoices/${id}`),
          api.get(`/settings/store`),
          api.get(`/settings/bank`),
          api.get(`/settings/invoice`),
        ]);
        setInv(i.data);
        setStore(st.data || store);
        setBank(bk.data || { show: false });
        setDeclaration(cfg.data.declaration || "");
        setSignature(cfg.data.signature_image || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = 210, pageH = 297;
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`${(inv.invoice_no || "invoice").replace(/[\\/]/g, "-")}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error("PDF generation failed:", e);
      toast.error("Could not generate PDF");
    } finally { setPdfLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" /></div>;
  if (!inv) return <div className="p-8">Invoice not found.</div>;

  const gstRate = Number(inv.gst_rate || 0);

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-6" data-testid="invoice-receipt">
      <style>{`@media print {
        @page { size: ${pageSize}; margin: ${pageSize === "A5" ? "6mm" : "12mm"}; }
        .no-print { display: none !important; }
        ${pageSize === "A5" ? ".print-area { zoom: 0.66; }" : ""}
      }`}</style>

      <div className="no-print max-w-4xl mx-auto px-6 mb-4 flex items-center justify-between gap-3">
        <Link to="/invoices">
          <Button variant="outline" className="gap-2" data-testid="back-to-invoices"><ArrowLeft className="w-4 h-4" /> Back</Button>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => printNow("A5")} className="gap-2" data-testid="print-invoice-a5-btn">
            <Printer className="w-4 h-4" /> Print (A5)
          </Button>
          <Button variant="outline" onClick={downloadPdf} disabled={pdfLoading} className="gap-2" data-testid="download-invoice-pdf-btn">
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download PDF (A4)
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-area mx-auto max-w-[210mm] bg-white shadow-sm border border-[#E2E8F0] print:shadow-none print:border-0">
        <div className="px-8 py-7 text-black text-[12px] flex flex-col min-h-[273mm]">
          {/* Heading */}
          <div className="text-center mb-2">
            <div className="text-lg font-bold uppercase tracking-[0.2em]" style={{ color: NAVY }}>Tax Invoice</div>
          </div>

          {/* Company header */}
          <div className="border-2 rounded-t" style={{ borderColor: NAVY }}>
            <div className="flex justify-between items-start p-3" style={{ borderBottom: `1px solid ${NAVY}` }}>
              <div className="min-w-0">
                <div className="text-xl font-display font-bold tracking-tight" style={{ color: NAVY }}>{store.name}</div>
                {store.address && <div className="text-[11px] text-[#334155] whitespace-pre-line leading-snug mt-0.5">{store.address}</div>}
                <div className="text-[11px] text-[#334155] mt-0.5">
                  {store.phone && <span>Tel: {store.phone}</span>}
                  {store.phone && store.email && <span> · </span>}
                  {store.email && <span>{store.email}</span>}
                </div>
                {store.gstin && <div className="text-[11px] mt-0.5"><span className="text-[#475569]">GSTIN: </span><span className="font-mono font-semibold">{store.gstin}</span></div>}
                {store.state && <div className="text-[11px]"><span className="text-[#475569]">State: </span>{store.state}{store.state_code ? `, Code: ${store.state_code}` : ""}</div>}
              </div>
              <div className="text-right shrink-0 pl-4 text-[11px]">
                <table className="text-[11px]">
                  <tbody>
                    <tr><td className="pr-2 text-[#475569] text-right">Invoice No</td><td className="font-mono font-semibold text-left">{inv.invoice_no}</td></tr>
                    <tr><td className="pr-2 text-[#475569] text-right">Date</td><td className="font-mono text-left">{inv.invoice_date}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bill to */}
            <div className="p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569] mb-1">Bill To</div>
              <div className="font-semibold text-[13px]">{inv.customer_name}</div>
              {inv.customer_address && <div className="text-[11px] text-[#334155] whitespace-pre-line leading-snug">{inv.customer_address}</div>}
              {inv.mobile && <div className="font-mono text-[11px]">{inv.mobile}</div>}
              {inv.customer_gst && <div className="text-[11px]"><span className="text-[#475569]">GSTIN: </span><span className="font-mono">{inv.customer_gst}</span></div>}
              {inv.customer_state && <div className="text-[11px]"><span className="text-[#475569]">State: </span>{inv.customer_state}{inv.customer_state_code ? `, Code: ${inv.customer_state_code}` : ""}</div>}
            </div>
          </div>

          {/* Items table */}
          <table className="w-full border-collapse text-[11px] border-2 border-t-0" style={{ borderColor: NAVY }}>
            <thead>
              <tr className="text-left" style={{ backgroundColor: "#EEF2FA" }}>
                <th className="border border-[#CBD5E1] px-2 py-1.5 w-8 text-center">Sl</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5">Description of Goods</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-center w-20">HSN/SAC</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-center w-20">Qty</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-right w-24">Rate</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-right w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={it.uid || it.inventory_id || it.item_master_id || `${it.item_name || "item"}-${i}`}>
                  <td className="border border-[#CBD5E1] px-2 py-1.5 text-center align-top">{i + 1}</td>
                  <td className="border border-[#CBD5E1] px-2 py-1.5">
                    {it.item_name && <div className="font-semibold">{it.item_name}</div>}
                    {it.description && <div className="whitespace-pre-line text-[#334155] text-[10.5px] leading-snug">{it.description}</div>}
                    {!it.item_name && !it.description && <span>—</span>}
                  </td>
                  <td className="border border-[#CBD5E1] px-2 py-1.5 text-center font-mono align-top">{it.hsn || ""}</td>
                  <td className="border border-[#CBD5E1] px-2 py-1.5 text-center align-top">{it.qty} {it.unit || "Nos"}</td>
                  <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono align-top">{rupee(it.unit_price)}</td>
                  <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono align-top">{rupee(it.amount)}</td>
                </tr>
              ))}
              {/* Tax rows */}
              <tr>
                <td className="border border-[#CBD5E1] px-2 py-1" colSpan={5} style={{ textAlign: "right" }}>Taxable Value</td>
                <td className="border border-[#CBD5E1] px-2 py-1 text-right font-mono">{rupee(inv.subtotal)}</td>
              </tr>
              {inv.intra_state ? (
                <>
                  <tr>
                    <td className="border border-[#CBD5E1] px-2 py-1" colSpan={5} style={{ textAlign: "right" }}>CGST @ {gstRate / 2}%</td>
                    <td className="border border-[#CBD5E1] px-2 py-1 text-right font-mono">{rupee(inv.cgst)}</td>
                  </tr>
                  <tr>
                    <td className="border border-[#CBD5E1] px-2 py-1" colSpan={5} style={{ textAlign: "right" }}>SGST @ {gstRate / 2}%</td>
                    <td className="border border-[#CBD5E1] px-2 py-1 text-right font-mono">{rupee(inv.sgst)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td className="border border-[#CBD5E1] px-2 py-1" colSpan={5} style={{ textAlign: "right" }}>IGST @ {gstRate}%</td>
                  <td className="border border-[#CBD5E1] px-2 py-1 text-right font-mono">{rupee(inv.igst)}</td>
                </tr>
              )}
              <tr className="font-bold" style={{ backgroundColor: "#EEF2FA", color: NAVY }}>
                <td className="border border-[#CBD5E1] px-2 py-1.5" colSpan={5} style={{ textAlign: "right" }}>Grand Total</td>
                <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono">{rupee(inv.grand_total)}</td>
              </tr>
            </tbody>
          </table>

          {/* Amount in words */}
          <div className="border-2 border-t-0 px-3 py-2 text-[11px]" style={{ borderColor: NAVY }}>
            <span className="text-[#475569]">Amount Chargeable (in words): </span>
            <span className="font-semibold">{numToWords(inv.grand_total)}</span>
          </div>

          {inv.notes && (
            <div className="mt-3 text-[11px]">
              <span className="text-[#475569]">Remarks: </span>{inv.notes}
            </div>
          )}

          {/* Bottom block — pinned to page bottom */}
          <div className="mt-auto pt-8">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                {bank?.show && (bank.ac_no || bank.bank_name || bank.upi_id) && (
                  <div className="text-[11px]">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569] mb-0.5">Bank Details</div>
                    {bank.ac_holder && <div><span className="text-[#475569]">A/c Holder: </span>{bank.ac_holder}{bank.branch ? ` (${bank.branch})` : ""}</div>}
                    {(bank.bank_name || bank.ac_no) && (
                      <div>
                        {bank.bank_name && <><span className="text-[#475569]">Bank: </span>{bank.bank_name}</>}
                        {bank.bank_name && bank.ac_no && <span className="text-[#CBD5E1]">  |  </span>}
                        {bank.ac_no && <><span className="text-[#475569]">A/c No: </span><span className="font-mono">{bank.ac_no}</span></>}
                      </div>
                    )}
                    {(bank.ifsc || bank.upi_id) && (
                      <div>
                        {bank.ifsc && <><span className="text-[#475569]">IFSC: </span><span className="font-mono">{bank.ifsc}</span></>}
                        {bank.ifsc && bank.upi_id && <span className="text-[#CBD5E1]">  |  </span>}
                        {bank.upi_id && <><span className="text-[#475569]">UPI: </span><span className="font-mono">{bank.upi_id}</span></>}
                      </div>
                    )}
                  </div>
                )}
                {declaration && (
                  <div className="text-[10.5px]">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569] mb-0.5">Declaration</div>
                    <div className="text-[#334155] whitespace-pre-line leading-snug">{declaration}</div>
                  </div>
                )}
              </div>
              <div className="flex items-end justify-between text-[11px] pt-10">
                <div className="text-center">
                  <div className="border-t border-[#334155] w-32 pt-1 text-[10px] text-[#475569]">Customer's Seal &amp; Signature</div>
                </div>
                <div className="text-center">
                  {signature && <img src={signature} alt="Authorised signatory" className="h-12 mx-auto object-contain mb-1" data-testid="invoice-signature-img" />}
                  <div className="border-t border-[#334155] w-32 pt-1 text-[10px] text-[#475569]">Authorised Signatory</div>
                </div>
              </div>
            </div>
            <div className="text-center text-[10px] text-[#94A3B8] mt-4 pt-2 border-t border-[#E2E8F0]">This is a computer-generated invoice.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
